import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    Plus,
    Link2,
    Link2Off,
    Loader2,
    Clock,
    MapPin,
    User,
    AlertCircle,
    CheckCircle,
    Settings,
    ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, parseISO, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';

// User colors for calendar events
const USER_COLORS = [
    { bg: 'bg-blue-500', light: 'bg-blue-100 dark:bg-blue-900/50', text: 'text-blue-700 dark:text-blue-300' },
    { bg: 'bg-green-500', light: 'bg-green-100 dark:bg-green-900/50', text: 'text-green-700 dark:text-green-300' },
    { bg: 'bg-purple-500', light: 'bg-purple-100 dark:bg-purple-900/50', text: 'text-purple-700 dark:text-purple-300' },
    { bg: 'bg-orange-500', light: 'bg-orange-100 dark:bg-orange-900/50', text: 'text-orange-700 dark:text-orange-300' },
    { bg: 'bg-pink-500', light: 'bg-pink-100 dark:bg-pink-900/50', text: 'text-pink-700 dark:text-pink-300' },
    { bg: 'bg-cyan-500', light: 'bg-cyan-100 dark:bg-cyan-900/50', text: 'text-cyan-700 dark:text-cyan-300' },
    { bg: 'bg-yellow-500', light: 'bg-yellow-100 dark:bg-yellow-900/50', text: 'text-yellow-700 dark:text-yellow-300' },
    { bg: 'bg-red-500', light: 'bg-red-100 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-300' },
];

const CalendarPage = () => {
    const { api, user, isSuperAdmin } = useAuth();
    const [searchParams] = useSearchParams();
    
    const [loading, setLoading] = useState(true);
    const [config, setConfig] = useState({ is_configured: false, is_connected: false });
    const [events, setEvents] = useState([]);
    const [calendarUsers, setCalendarUsers] = useState([]);
    const [currentWeek, setCurrentWeek] = useState(new Date());
    const [selectedUserId, setSelectedUserId] = useState('all');
    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    const [eventForm, setEventForm] = useState({
        title: '',
        description: '',
        location: '',
        start: '',
        end: '',
        is_all_day: false,
    });

    // Calculate week dates
    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    // Build user color map
    const userColorMap = useMemo(() => {
        const map = {};
        calendarUsers.forEach((u, index) => {
            map[u.id] = USER_COLORS[index % USER_COLORS.length];
        });
        return map;
    }, [calendarUsers]);

    useEffect(() => {
        fetchConfig();
        
        // Handle OAuth callback
        if (searchParams.get('success') === 'true') {
            toast.success('Google Calendar connecté avec succès !');
        } else if (searchParams.get('error')) {
            toast.error(`Erreur de connexion: ${searchParams.get('error')}`);
        }
    }, []);

    useEffect(() => {
        if (config.is_configured) {
            fetchEvents();
            fetchCalendarUsers();
        }
    }, [currentWeek, config.is_configured]);

    const fetchConfig = async () => {
        try {
            const response = await api.get('/calendar/config');
            setConfig(response.data);
        } catch (error) {
            console.error('Failed to fetch calendar config:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEvents = async () => {
        try {
            const params = new URLSearchParams({
                start_date: weekStart.toISOString(),
                end_date: weekEnd.toISOString(),
            });
            if (selectedUserId !== 'all') {
                params.append('user_id', selectedUserId);
            }
            
            const response = await api.get(`/calendar/events?${params.toString()}`);
            setEvents(response.data);
        } catch (error) {
            console.error('Failed to fetch events:', error);
        }
    };

    const fetchCalendarUsers = async () => {
        try {
            const response = await api.get('/calendar/users');
            setCalendarUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch calendar users:', error);
        }
    };

    const handleConnectGoogle = async () => {
        try {
            const response = await api.get('/oauth/calendar/login');
            window.location.href = response.data.authorization_url;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la connexion');
        }
    };

    const handleDisconnectGoogle = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir déconnecter votre Google Calendar ?')) return;
        
        try {
            await api.post('/oauth/calendar/disconnect');
            toast.success('Google Calendar déconnecté');
            setConfig(prev => ({ ...prev, is_connected: false }));
            fetchEvents();
            fetchCalendarUsers();
        } catch (error) {
            toast.error('Erreur lors de la déconnexion');
        }
    };

    const handleCreateEvent = async (e) => {
        e.preventDefault();
        if (!config.is_connected) {
            toast.error('Connectez votre Google Calendar pour créer des événements');
            return;
        }
        
        setSubmitting(true);
        try {
            await api.post('/calendar/events', eventForm);
            toast.success('Événement créé');
            setShowEventModal(false);
            resetEventForm();
            fetchEvents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteEvent = async (eventId) => {
        if (!window.confirm('Supprimer cet événement ?')) return;
        
        try {
            await api.delete(`/calendar/events/${eventId}`);
            toast.success('Événement supprimé');
            setSelectedEvent(null);
            fetchEvents();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };

    const resetEventForm = () => {
        setEventForm({
            title: '',
            description: '',
            location: '',
            start: '',
            end: '',
            is_all_day: false,
        });
    };

    const openNewEventModal = (date) => {
        const startDate = new Date(date);
        startDate.setHours(9, 0, 0, 0);
        const endDate = new Date(date);
        endDate.setHours(10, 0, 0, 0);
        
        setEventForm({
            title: '',
            description: '',
            location: '',
            start: format(startDate, "yyyy-MM-dd'T'HH:mm"),
            end: format(endDate, "yyyy-MM-dd'T'HH:mm"),
            is_all_day: false,
        });
        setShowEventModal(true);
    };

    const getEventsForDay = (date) => {
        return events.filter(event => {
            const eventDate = parseISO(event.start);
            return isSameDay(eventDate, date);
        });
    };

    const formatEventTime = (event) => {
        if (event.is_all_day) return 'Journée';
        const start = parseISO(event.start);
        return format(start, 'HH:mm');
    };

    // Loading state
    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-[600px] w-full" />
            </div>
        );
    }

    // Not configured state
    if (!config.is_configured) {
        return (
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold font-heading">Calendrier partagé</h1>
                    <p className="text-muted-foreground mt-1">Synchronisez vos rendez-vous avec Google Agenda</p>
                </div>
                
                <Alert variant="warning">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Configuration requise</AlertTitle>
                    <AlertDescription>
                        <p className="mb-4">
                            Google Calendar n'est pas encore configuré. Un administrateur doit ajouter les credentials Google dans les paramètres.
                        </p>
                        {isSuperAdmin() && (
                            <div className="p-4 bg-muted rounded-lg space-y-2">
                                <p className="font-medium">Instructions pour l'administrateur :</p>
                                <ol className="list-decimal list-inside space-y-1 text-sm">
                                    <li>Allez sur <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google Cloud Console</a></li>
                                    <li>Créez un projet et activez l'API Google Calendar</li>
                                    <li>Configurez l'écran de consentement OAuth</li>
                                    <li>Créez des credentials OAuth (Web application)</li>
                                    <li>Ajoutez l'URI de redirection : <code className="bg-background px-1 rounded">{window.location.origin}/api/oauth/calendar/callback</code></li>
                                    <li>Copiez le Client ID et Client Secret</li>
                                    <li>Ajoutez-les dans le fichier .env du backend :<br/>
                                        <code className="bg-background px-1 rounded">GOOGLE_CLIENT_ID=votre_client_id</code><br/>
                                        <code className="bg-background px-1 rounded">GOOGLE_CLIENT_SECRET=votre_client_secret</code>
                                    </li>
                                    <li>Redéployez l'application</li>
                                </ol>
                            </div>
                        )}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold font-heading">Calendrier partagé</h1>
                    <p className="text-muted-foreground mt-1">
                        Semaine du {format(weekStart, 'd MMMM', { locale: fr })} au {format(weekEnd, 'd MMMM yyyy', { locale: fr })}
                    </p>
                </div>
                
                <div className="flex items-center gap-2">
                    {config.is_connected ? (
                        <Button variant="outline" size="sm" onClick={handleDisconnectGoogle}>
                            <Link2Off className="w-4 h-4 mr-2" />
                            Déconnecter
                        </Button>
                    ) : (
                        <Button onClick={handleConnectGoogle}>
                            <Link2 className="w-4 h-4 mr-2" />
                            Connecter Google Calendar
                        </Button>
                    )}
                    
                    {config.is_connected && (
                        <Button onClick={() => openNewEventModal(new Date())}>
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau RDV
                        </Button>
                    )}
                </div>
            </div>

            {/* Connection status */}
            {config.is_connected && (
                <Alert>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <AlertDescription>
                        Connecté à Google Calendar ({config.user_google_email})
                    </AlertDescription>
                </Alert>
            )}

            {/* Filters and Navigation */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        {/* Week navigation */}
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="icon" onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}>
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" onClick={() => setCurrentWeek(new Date())}>
                                Aujourd'hui
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                        
                        {/* User filter */}
                        <div className="flex items-center gap-2">
                            <Label className="text-sm whitespace-nowrap">Filtrer par :</Label>
                            <Select value={selectedUserId} onValueChange={(v) => { setSelectedUserId(v); }}>
                                <SelectTrigger className="w-48">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les commerciaux</SelectItem>
                                    {calendarUsers.map((u) => (
                                        <SelectItem key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    {/* User legend */}
                    {calendarUsers.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                            {calendarUsers.map((u, index) => (
                                <Badge key={u.id} variant="outline" className={`${userColorMap[u.id]?.light} ${userColorMap[u.id]?.text}`}>
                                    <span className={`w-2 h-2 rounded-full ${userColorMap[u.id]?.bg} mr-2`} />
                                    {u.first_name} {u.last_name}
                                </Badge>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Calendar Grid */}
            <Card>
                <CardContent className="p-0">
                    <div className="grid grid-cols-7 border-b">
                        {weekDays.map((day, index) => (
                            <div
                                key={index}
                                className={`p-3 text-center border-r last:border-r-0 ${
                                    isSameDay(day, new Date()) ? 'bg-primary/10' : ''
                                }`}
                            >
                                <p className="text-xs text-muted-foreground uppercase">
                                    {format(day, 'EEE', { locale: fr })}
                                </p>
                                <p className={`text-lg font-semibold ${
                                    isSameDay(day, new Date()) ? 'text-primary' : ''
                                }`}>
                                    {format(day, 'd')}
                                </p>
                            </div>
                        ))}
                    </div>
                    
                    <div className="grid grid-cols-7 min-h-[500px]">
                        {weekDays.map((day, index) => {
                            const dayEvents = getEventsForDay(day);
                            
                            return (
                                <div
                                    key={index}
                                    className={`border-r last:border-r-0 p-2 ${
                                        isSameDay(day, new Date()) ? 'bg-primary/5' : ''
                                    }`}
                                >
                                    {/* Add event button */}
                                    {config.is_connected && (
                                        <button
                                            onClick={() => openNewEventModal(day)}
                                            className="w-full text-xs text-muted-foreground hover:text-primary hover:bg-muted p-1 rounded mb-2 transition-colors"
                                        >
                                            + Ajouter
                                        </button>
                                    )}
                                    
                                    {/* Events */}
                                    <div className="space-y-1">
                                        {dayEvents.map((event) => {
                                            const userColor = userColorMap[event.user_id] || USER_COLORS[0];
                                            return (
                                                <button
                                                    key={event.id}
                                                    onClick={() => setSelectedEvent(event)}
                                                    className={`w-full text-left p-2 rounded text-xs ${userColor.light} ${userColor.text} hover:opacity-80 transition-opacity`}
                                                >
                                                    <p className="font-medium truncate">{event.title}</p>
                                                    <p className="text-[10px] opacity-75">
                                                        {formatEventTime(event)} - {event.user_name}
                                                    </p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Event Details Modal */}
            <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{selectedEvent?.title}</DialogTitle>
                        <DialogDescription>
                            Par {selectedEvent?.user_name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedEvent && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-sm">
                                <Clock className="w-4 h-4 text-muted-foreground" />
                                <span>
                                    {selectedEvent.is_all_day ? (
                                        format(parseISO(selectedEvent.start), 'EEEE d MMMM yyyy', { locale: fr })
                                    ) : (
                                        <>
                                            {format(parseISO(selectedEvent.start), 'EEEE d MMMM yyyy HH:mm', { locale: fr })}
                                            {' - '}
                                            {format(parseISO(selectedEvent.end), 'HH:mm', { locale: fr })}
                                        </>
                                    )}
                                </span>
                            </div>
                            
                            {selectedEvent.location && (
                                <div className="flex items-center gap-2 text-sm">
                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                    <span>{selectedEvent.location}</span>
                                </div>
                            )}
                            
                            {selectedEvent.description && (
                                <p className="text-sm text-muted-foreground">
                                    {selectedEvent.description}
                                </p>
                            )}
                            
                            <div className="flex items-center gap-2 text-sm">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <span>{selectedEvent.user_email}</span>
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        {selectedEvent?.user_id === user?.id && (
                            <Button variant="destructive" onClick={() => handleDeleteEvent(selectedEvent.id)}>
                                Supprimer
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => setSelectedEvent(null)}>
                            Fermer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Event Modal */}
            <Dialog open={showEventModal} onOpenChange={setShowEventModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Nouveau rendez-vous</DialogTitle>
                        <DialogDescription>
                            Créer un événement dans votre Google Calendar
                        </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleCreateEvent} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Titre *</Label>
                            <Input
                                value={eventForm.title}
                                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                                placeholder="Titre du rendez-vous"
                                required
                            />
                        </div>
                        
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="all_day"
                                checked={eventForm.is_all_day}
                                onCheckedChange={(checked) => setEventForm({ ...eventForm, is_all_day: checked })}
                            />
                            <Label htmlFor="all_day">Journée entière</Label>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Début *</Label>
                                <Input
                                    type={eventForm.is_all_day ? "date" : "datetime-local"}
                                    value={eventForm.is_all_day ? eventForm.start.slice(0, 10) : eventForm.start}
                                    onChange={(e) => setEventForm({ ...eventForm, start: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Fin *</Label>
                                <Input
                                    type={eventForm.is_all_day ? "date" : "datetime-local"}
                                    value={eventForm.is_all_day ? eventForm.end.slice(0, 10) : eventForm.end}
                                    onChange={(e) => setEventForm({ ...eventForm, end: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Lieu</Label>
                            <Input
                                value={eventForm.location}
                                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                                placeholder="Adresse ou lien de visio"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={eventForm.description}
                                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                                placeholder="Notes ou détails..."
                                rows={3}
                            />
                        </div>
                        
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowEventModal(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting}>
                                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Créer
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CalendarPage;
